/* eslint-disable @typescript-eslint/no-explicit-any */

import { FormEvent, useEffect, useState } from 'react';
import {
  AutoCompleteField,
  Button,
  Container,
  ExportTableButton,
} from '@/components';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteChangeEvent,
} from 'primereact/autocomplete';
import { RadioButton } from 'primereact/radiobutton';
import { TableData } from '@/components/Table/types';
import { formatDate } from '@/utils/formatDate';
import { getTrips } from '../../services/getTrips';
import { getUnloadingList } from './getUnloadingList';
import UnloadingListTable from './UnloadingListTable/UnloadingListTable';
import { columnsToExport } from './columnsToExport';
import styles from './UnloadingList.module.scss';
import { UnloadingInfo } from './types';

enum UserType {
  Armador = 'Armador',
  ArmadorSlot = 'Armador de slot',
}

const FILE_NAME = 'UnloadingList-Montecon';

const UnloadingList = () => {
  const { t } = useTranslation('unloadingList');

  const { data: tripsData } = useQuery({
    queryKey: ['trips'],
    queryFn: getTrips,
  });

  const getUnloadingListMutation = useMutation({
    mutationFn: getUnloadingList,
  });

  const [filteredTrips, setFilteredTrips] = useState<string[] | undefined>([]);
  const [selectedTrip, setSelectedTrip] = useState('');
  const [tripCode, setTripCode] = useState<number | null>(null);
  const [selectedUserType, setSelectedUserType] = useState(UserType.Armador);

  const trips = tripsData
    ?.slice()
    .sort(
      (a, b) =>
        new Date(b.comienzoOperaciones).getTime() -
        new Date(a.comienzoOperaciones).getTime()
    )
    .map((item) => {
      let value = item.nombre;

      const date = new Date(item?.comienzoOperaciones);

      const dateFormatted = formatDate(date);

      value = `${value} - ${dateFormatted}`;

      return value;
    });

  const searchTrips = (event: AutoCompleteCompleteEvent) => {
    const filteredTripNames = trips?.filter((name) =>
      name.toLowerCase().includes(event.query.toLowerCase())
    );

    setFilteredTrips(filteredTripNames);
  };

  const handleTripChange = (event: AutoCompleteChangeEvent) => {
    const tripName = event.value;
    setSelectedTrip(tripName);

    const code = tripsData?.find((trip) => {
      const name = (tripName || '').split(' - ')[0];

      return name === trip.nombre;
    })?.codigo;

    if (code) {
      setTripCode(code);
    } else {
      setTripCode(null);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (tripCode) {
      getUnloadingListMutation.mutate({
        codViaje: tripCode,
        porSlot: selectedUserType === UserType.ArmadorSlot,
      });
    }
  };

  const submitButtonText = t('submitButton');

  const isTableHidden =
    !getUnloadingListMutation.data && !getUnloadingListMutation.isPending;

  const [parsedData, setParsedData] = useState<UnloadingInfo[]>([]);

  useEffect(() => {
    if (getUnloadingListMutation.data) {
      const newData = getUnloadingListMutation.data.map((item: any) => {
        const { ...rest } = item;

        const temperatureWithoutC = item?.temperatura
          ? item?.temperatura
              .replace('CELSIUS', '')
              .replace('C', '')
              .replace(',', '.')
          : null;

        const temp =
          temperatureWithoutC !== null ? Number(temperatureWithoutC) : null;

        return {
          ...rest,
          temperatura: temp,
        };
      });
      setParsedData(newData);
    }
  }, [getUnloadingListMutation.data]);

  return (
    <Container title={t('title')} className={styles.unloadingList}>
      <header>
        <form onSubmit={handleSubmit}>
          <AutoCompleteField
            className={styles.autoCompleteField}
            label={t('tripLabel')}
            placeholder={t('tripPlaceholder')}
            value={selectedTrip}
            onChange={handleTripChange}
            suggestions={filteredTrips}
            completeMethod={searchTrips}
            field="trip"
            id="trip"
            required
          />
          <div className={styles.radioButtonContainer}>
            <RadioButton
              value={UserType.Armador}
              name="userType"
              inputId="armador"
              onChange={(e) => setSelectedUserType(e.value)}
              checked={selectedUserType === UserType.Armador}
            />
            <label htmlFor="armador">{t('shipowner')}</label>
          </div>
          <div className={styles.radioButtonContainer}>
            <RadioButton
              value={UserType.ArmadorSlot}
              name="userType"
              inputId="armadorSlot"
              onChange={(e) => setSelectedUserType(e.value)}
              checked={selectedUserType === UserType.ArmadorSlot}
            />
            <label htmlFor="armadorSlot">{t('charterer')}</label>
          </div>
          <Button
            type="submit"
            className={styles.submitButton}
            disabled={!tripCode}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.icon} />
            {submitButtonText}
          </Button>
        </form>
        {!isTableHidden && (
          <ExportTableButton
            data={getUnloadingListMutation.data as TableData}
            isDisabled={getUnloadingListMutation.isPending}
            columnsToExport={columnsToExport}
            fileName={FILE_NAME}
          />
        )}
      </header>
      {!isTableHidden && (
        <UnloadingListTable
          data={parsedData}
          isLoading={getUnloadingListMutation.isPending}
        />
      )}
    </Container>
  );
};

export default UnloadingList;
